import { defineStore } from "pinia";
import { ref, computed, readonly } from "vue";
import { logger } from "@/infrastructure/services/LoggerService";
import { getApiErrorCode } from "@/shared/utils/errorUtils";
import i18n from "@/plugins/i18n";
export const useErrorStore = defineStore("error", () => {
    // State
    const currentError = ref(null);
    const errorHistory = ref([]);
    // Helper functions
    function determineErrorType(err) {
        if (err?.request && !err?.response)
            return "NETWORK_ERROR";
        if (err?.response?.status === 401)
            return "AUTHENTICATION_ERROR";
        if (err?.response?.status === 403)
            return "AUTHORIZATION_ERROR";
        if (err?.response?.status >= 500)
            return "SERVER_ERROR";
        if (err?.response?.status >= 400)
            return "API_ERROR";
        return "UNKNOWN_ERROR";
    }
    function determineSeverity(err) {
        if (err?.response?.status >= 500)
            return "critical";
        if (err?.response?.status === 401 || err?.response?.status === 403)
            return "high";
        if (err?.request && !err?.response)
            return "medium";
        return "low";
    }
    /**
     * Add error to history and maintain size limit
     */
    function addToHistory(errorState) {
        currentError.value = errorState;
        errorHistory.value.unshift(errorState);
        // Keep only last 10 errors
        if (errorHistory.value.length > 10) {
            errorHistory.value = errorHistory.value.slice(0, 10);
        }
        logger.debug("Error logged:", errorState);
    }
    // Actions
    function error(input) {
        let msg = "An unexpected error occurred.";
        let errorCode;
        let translationKey;
        let translationParams;
        if (typeof input === "string") {
            msg = input;
        }
        else if (typeof input === "object" && input !== null) {
            const err = input;
            // Handle new error response format
            const apiErrorCode = getApiErrorCode(err);
            if (apiErrorCode !== undefined && err.response?.data?.success === false) {
                errorCode = apiErrorCode;
                msg = `An error occurred (${err.response.data?.title}). Please contact the support team for assistance.`;
            }
            // AxiosError with response
            else if (err.response?.status) {
                const status = err.response.status;
                errorCode = status;
                const { t } = i18n.global;
                switch (status) {
                    case 400:
                        translationKey = "error.http.400";
                        msg = t(translationKey);
                        break;
                    case 401:
                        translationKey = "error.http.401";
                        msg = t(translationKey);
                        break;
                    case 403:
                        translationKey = "error.http.403";
                        msg = t(translationKey);
                        break;
                    case 404:
                        translationKey = "error.http.404";
                        msg = t(translationKey);
                        break;
                    case 500:
                        translationKey = "error.http.500";
                        msg = t(translationKey);
                        break;
                    case 502:
                        translationKey = "error.http.502";
                        msg = t(translationKey);
                        break;
                    case 503:
                        translationKey = "error.http.503";
                        msg = t(translationKey);
                        break;
                    case 504:
                        translationKey = "error.http.504";
                        msg = t(translationKey);
                        break;
                    default:
                        translationKey = "error.http.default";
                        translationParams = { status };
                        msg = t(translationKey, translationParams);
                        break;
                }
            }
            // AxiosError with request but no response (e.g., network error)
            else if (err.request) {
                msg = "No response from server - please check your network.";
                errorCode = "NETWORK_ERROR";
            }
            // Standard JS error message
            else if (err.message) {
                msg = err.message;
            }
        }
        // Create error state
        const errorState = {
            message: msg,
            code: errorCode,
            timestamp: Date.now(),
            severity: determineSeverity(input),
            type: determineErrorType(input),
            translationKey,
            translationParams,
        };
        // Add to history
        addToHistory(errorState);
        return errorState;
    }
    function clearCurrentError() {
        currentError.value = null;
    }
    function clearErrorHistory() {
        errorHistory.value = [];
    }
    /**
     * Set a payment refused error with specific UI handling
     * @param cancelUrl - URL to redirect when user cancels
     * @param retryUrl - URL to redirect when user wants to retry
     */
    function setPaymentRefusedError(cancelUrl, retryUrl) {
        const { t } = i18n.global;
        // Build buttons configuration
        const buttons = [
            {
                labelKey: "payment.error.retry",
                action: "retry",
                url: retryUrl,
                style: "primary",
                uppercase: true,
            },
        ];
        // Add cancel button only if cancelUrl is provided
        if (cancelUrl) {
            buttons.push({
                labelKey: "payment.error.cancelAndReturn",
                action: "cancel",
                url: cancelUrl,
                style: "link",
            });
        }
        const errorState = {
            message: t("payment.error.message"),
            code: "PAYMENT_REFUSED",
            timestamp: Date.now(),
            severity: "medium",
            type: "PAYMENT_ERROR",
            translationKey: "payment.error.message",
            titleKey: "payment.error.title",
            hideDetails: true,
            buttons,
        };
        // Add to history
        addToHistory(errorState);
        return errorState;
    }
    /**
     * Set a payment canceled error with specific UI handling
     * @param cancelUrl - URL to redirect to merchant site
     */
    function setPaymentCanceledError(cancelUrl) {
        const { t } = i18n.global;
        // Build buttons configuration - always show a button
        const buttons = [];
        if (cancelUrl) {
            // If cancelUrl exists, show "Return to the merchant site" button
            buttons.push({
                labelKey: "payment.canceled.returnToMerchantSite",
                action: "link",
                url: cancelUrl,
                style: "primary",
            });
        }
        else {
            // If no cancelUrl, show "Return to the website" button linking to session-ended page
            // This is a public page that doesn't require authentication
            buttons.push({
                labelKey: "payment.canceled.returnToWebsite",
                action: "link",
                url: "/session-ended",
                style: "primary",
            });
        }
        const errorState = {
            message: t("payment.canceled.messageWithUrl"),
            code: "PAYMENT_CANCELED",
            timestamp: Date.now(),
            severity: "medium",
            type: "PAYMENT_ERROR",
            translationKey: "payment.canceled.messageWithUrl",
            titleKey: "payment.canceled.title",
            hideDetails: true,
            buttons,
        };
        // Add to history
        addToHistory(errorState);
        return errorState;
    }
    // Getters
    const hasError = computed(() => currentError.value !== null);
    const latestError = computed(() => errorHistory.value[0] || null);
    return {
        // State
        currentError: readonly(currentError),
        errorHistory: readonly(errorHistory),
        // Getters
        hasError,
        latestError,
        // Actions
        error,
        setPaymentRefusedError,
        setPaymentCanceledError,
        clearCurrentError,
        clearErrorHistory,
    };
});
